/**
 * mcsAutoFetch.js - Master Context Sheet Auto-Fetch Service
 * Baba AI - Self-sustaining context loading
 * 
 * Features:
 * - Auto-detect MCS files in Google Drive (MCS_v*.md pattern)
 * - Fetch highest version automatically
 * - Cache locally for offline access
 * - Inject into system prompt
 * - Self-sustaining: works without user interaction
 * 
 * Searches directly for MCS_v* files without folder restrictions.
 * MCS files can be anywhere in the connected Google Drive.
 * 
 * Updated: November 26, 2025
 */

import googleDriveService from './googleDriveService';

class MCSAutoFetch {
  constructor() {
    this.storageKey = 'baba-mcs-cache';
    this.lastFetchKey = 'baba-mcs-last-fetch';
    this.currentMCS = null;
    this.fetchInterval = 3600000; // 1 hour between fetches
  }

  // ==========================================
  // AUTO-FETCH LOGIC
  // ==========================================

  /**
   * Main entry point - call on app startup
   * Returns MCS content for system prompt injection
   */
  async fetchMCS() {
    console.log('[MCSAutoFetch] Starting auto-fetch...');
    
    // Check if we need to fetch (rate limiting)
    if (!this.shouldFetch()) {
      console.log('[MCSAutoFetch] Using cached MCS (recent fetch)');
      return this.getCachedMCS();
    }

    // Check if Google Drive is authenticated
    if (!googleDriveService.isAuthenticated()) {
      console.log('[MCSAutoFetch] GDrive not authenticated, using cache');
      return this.getCachedMCS();
    }

    try {
      // Search for MCS files directly (no folder restriction)
      // This works because we're searching by name pattern
      const mcsFiles = await googleDriveService.searchFiles('MCS_v');

      if (!mcsFiles || mcsFiles.length === 0) {
        console.log('[MCSAutoFetch] No MCS files found, using cache');
        return this.getCachedMCS();
      }

      // Filter for actual MCS files (MCS_v*.md pattern) and Google Docs
      const validMcsFiles = mcsFiles.filter(file => 
        file.name && file.name.match(/^MCS_v\d+[_\.]\d+/)
      );

      if (validMcsFiles.length === 0) {
        console.log('[MCSAutoFetch] No valid MCS files found, using cache');
        return this.getCachedMCS();
      }

      // Find highest version
      const highestVersion = this.findHighestVersion(validMcsFiles);
      
      if (!highestVersion) {
        console.log('[MCSAutoFetch] Could not determine highest version');
        return this.getCachedMCS();
      }

      console.log(`[MCSAutoFetch] Found highest version: ${highestVersion.name}`);

      // Fetch content
      const content = await this.fetchFileContent(highestVersion.id);
      
      if (content) {
        // Cache it
        this.cacheMCS(content, highestVersion.name);
        this.currentMCS = content;
        console.log('[MCSAutoFetch] ✅ MCS fetched and cached successfully');
        return content;
      }

    } catch (error) {
      console.error('[MCSAutoFetch] Error fetching MCS:', error);
    }

    // Fallback to cache
    return this.getCachedMCS();
  }

  /**
   * Find highest version from file list
   * Handles: MCS_v2_9.md, MCS_v2_10.md, MCS_v3_0.md etc.
   */
  findHighestVersion(files) {
    let highest = null;
    let highestScore = 0;

    for (const file of files) {
      // Extract version from filename
      // Pattern: MCS_v{major}_{minor}.md or MCS_v{major}.{minor}.md
      const match = file.name.match(/MCS_v(\d+)[._](\d+)/i);
      
      if (match) {
        const major = parseInt(match[1]);
        const minor = parseInt(match[2]);
        const score = major * 1000 + minor; // v2_10 = 2010, v3_0 = 3000
        
        if (score > highestScore) {
          highestScore = score;
          highest = file;
        }
      }
    }

    return highest;
  }

  /**
   * Fetch file content from Google Drive
   */
  async fetchFileContent(fileId) {
    try {
      // For Google Docs, export as plain text
      const response = await googleDriveService.getFileContent(fileId);
      return response;
    } catch (error) {
      console.error('[MCSAutoFetch] Error fetching file content:', error);
      
      // Try alternative: export as text
      try {
        const exportResponse = await googleDriveService.exportFile(fileId, 'text/plain');
        return exportResponse;
      } catch (exportError) {
        console.error('[MCSAutoFetch] Export also failed:', exportError);
        return null;
      }
    }
  }

  // ==========================================
  // CACHING
  // ==========================================

  /**
   * Check if we should fetch (rate limiting)
   */
  shouldFetch() {
    const lastFetch = localStorage.getItem(this.lastFetchKey);
    if (!lastFetch) return true;
    
    const elapsed = Date.now() - parseInt(lastFetch);
    return elapsed > this.fetchInterval;
  }

  /**
   * Cache MCS locally
   */
  cacheMCS(content, filename) {
    try {
      const cache = {
        content: content,
        filename: filename,
        fetchedAt: Date.now()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(cache));
      localStorage.setItem(this.lastFetchKey, Date.now().toString());
    } catch (error) {
      console.error('[MCSAutoFetch] Cache error:', error);
    }
  }

  /**
   * Get cached MCS
   */
  getCachedMCS() {
    try {
      const cached = localStorage.getItem(this.storageKey);
      if (cached) {
        const data = JSON.parse(cached);
        this.currentMCS = data.content;
        console.log(`[MCSAutoFetch] Loaded from cache: ${data.filename}`);
        return data.content;
      }
    } catch (error) {
      console.error('[MCSAutoFetch] Cache read error:', error);
    }
    return null;
  }

  /**
   * Get cache info
   */
  getCacheInfo() {
    try {
      const cached = localStorage.getItem(this.storageKey);
      if (cached) {
        const data = JSON.parse(cached);
        return {
          filename: data.filename,
          fetchedAt: new Date(data.fetchedAt).toLocaleString(),
          age: Math.round((Date.now() - data.fetchedAt) / 60000) + ' minutes ago',
          contentLength: data.content?.length || 0
        };
      }
    } catch (error) {
      console.error('[MCSAutoFetch] Cache info error:', error);
    }
    return null;
  }

  // ==========================================
  // PROMPT INJECTION
  // ==========================================

  /**
   * Format MCS for system prompt injection
   */
  formatForPrompt() {
    if (!this.currentMCS) return null;
    
    // Truncate if too long (keep under 10K tokens ~40K chars)
    let content = this.currentMCS;
    if (content.length > 40000) {
      content = content.substring(0, 40000) + '\n\n[MCS truncated for context window]';
    }

    return `<master_context_sheet>
${content}
</master_context_sheet>`;
  }

  /**
   * Get current MCS (from memory or cache)
   */
  getCurrentMCS() {
    if (this.currentMCS) return this.currentMCS;
    return this.getCachedMCS();
  }

  /**
   * Force refresh (bypass rate limiting)
   */
  async forceRefresh() {
    localStorage.removeItem(this.lastFetchKey);
    return await this.fetchMCS();
  }

  // ==========================================
  // STATUS & DIAGNOSTICS
  // ==========================================

  /**
   * Check if MCS is loaded
   */
  isLoaded() {
    return this.currentMCS !== null || this.getCachedMCS() !== null;
  }

  /**
   * Get status for System Check
   */
  getStatus() {
    const cache = this.getCacheInfo();
    return {
      loaded: this.isLoaded(),
      cached: cache !== null,
      cacheInfo: cache,
      currentLength: this.currentMCS?.length || 0
    };
  }
}

// Singleton
const mcsAutoFetch = new MCSAutoFetch();

export default mcsAutoFetch;
